export function formatThousands(value: string | number): string {
  const raw = typeof value === "number" ? String(value) : value.trim();
  const num = Number(raw);

  if (!Number.isFinite(num)) {
    return typeof value === "number" ? String(value) : value;
  }

  return new Intl.NumberFormat("en-US").format(num);
}

/**
 * 将长地址压缩为 `前缀...后缀` 的展示格式。
 *
 * @param value 原始地址字符串，例如 `0x09c0d8xxxxxxxxe89827`
 * @param prefixLength 保留前缀长度，默认 `8`
 * @param suffixLength 保留后缀长度，默认 `6`
 * @returns 若地址长度足够则返回压缩格式；否则返回原始（trim 后）字符串
 */
export function formatAddress(
  value: string,
  prefixLength = 8,
  suffixLength = 6,
): string {
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (trimmed.length <= prefixLength + suffixLength) return trimmed;

  return `${trimmed.slice(0, prefixLength)}...${trimmed.slice(-suffixLength)}`;
}

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DOMAIN_LABEL_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
const IPV4_HOSTNAME_REGEX = /^\d{1,3}(\.\d{1,3}){3}$/;

/** Accepts `example.com`, `www.example.com`, or `https://example.com/path`. */
export function isValidWebsiteUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;

  const hostPart =
    trimmed
      .replace(/^https?:\/\//i, "")
      .split("/")[0]
      ?.split("?")[0]
      ?.split("#")[0]
      ?.split(":")[0] ?? "";

  // `new URL("https://123")` parses as IPv4 `0.0.0.123` — reject bare numbers early.
  if (/^\d+$/.test(hostPart)) return false;

  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const { protocol, hostname } = new URL(withScheme);
    if (protocol !== "http:" && protocol !== "https:") return false;
    if (!hostname.includes(".")) return false;
    if (hostname.startsWith(".") || hostname.endsWith(".")) return false;
    if (IPV4_HOSTNAME_REGEX.test(hostname)) return false;

    const labels = hostname.split(".").filter(Boolean);
    if (labels.length < 2) return false;

    const tld = labels[labels.length - 1];
    if (!/^[a-zA-Z]{2,63}$/.test(tld)) return false;

    return labels.every(
      (label) =>
        DOMAIN_LABEL_REGEX.test(label) ||
        (label.length === 1 && /^[a-zA-Z0-9]$/.test(label)),
    );
  } catch {
    return false;
  }
}
